/**
 * -----------------------------------------------------------------------------
 * Popup - Send Location of vehicle to phone number
 * -----------------------------------------------------------------------------
 * This popup is used for Send location of vehicle to phone number
 */

import { Formik, useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useSendMessageMutation } from "../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../api/network/errorCodes";
import { TModalsState, setModalsData } from "../../api/store/commonSlice";
import {
  AdminFormFieldInput,
  AdminFormFieldSubmit,
} from "../../components/admin/formFields";
import {
  SendLocationValidationSchema,
  sendTextFormInitialValues,
  TFormFieldNames,
  YupValidationSchema,
} from "./validation";
import { GOOGLEMAPS_ENDPOINTS } from "../../api/network/endpoints";
import { APP_CONFIG } from "../../constants/constants";
import FTModal from "../../components/common/FTModal";
import { useMemo } from "react";
import FormActionButtons from "../../components/admin/formActionButtons";

interface ModalSendLocationProps {
  latitude: number;
  longitude: number;
}

const ModalSendLocation = (props: ModalSendLocationProps) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "mapFleet.sendLocation",
  });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();

  // user mutation
  const [senMessageTrigger] = useSendMessageMutation();

  const hideModal = () => {
    resetForm();
    dispatch(setModalsData({ ...modalsState, showSendLocation: false }));
  };
  const formik = useFormik({
    initialValues: sendTextFormInitialValues,
    validateOnChange: true,
    validationSchema: SendLocationValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      // if(APP_CONFIG.TOASTS.INFO) toast.info(t('loginScreen.toast.logging_you_in'));
      senMessageTrigger({
        phone: `${APP_CONFIG.COUNTRY_CODE}${values.phone}`,
        body: GOOGLEMAPS_ENDPOINTS.PLACE_URL(props.latitude, props.longitude),
      })
        .unwrap()
        .then((data) => {
          if (!!data?.message) {
            resetForm();
            dispatch(
              setModalsData({ ...modalsState, showSendLocation: false })
            );
            toast.success(t("toast.send_success"), { autoClose: 10000 });
          } else {
            toast.error(t("toast.send_failed"));
          }
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.send_failed")];
          toast.error(errors?.join(" "));
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    dirty,
    setFieldValue,
    resetForm,
  } = formik;
  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNames]
  );
  const isFormValid = invalidFields.length === 0 && dirty;
  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              label: t("send"),
              variant: "primary",
              disabled: !isFormValid || isSubmitting,
            },
            {
              label: tMain("cancel"),
              type: "button",
              variant: "danger-transparent",
              disabled: isSubmitting /* || isLoading*/,
              onClick: () => hideModal(),
            },
          ]}
        />
      </>
    );
  }, [isFormValid, isSubmitting]);

  if (
    modalsState.showSendLocation === false ||
    !props?.latitude ||
    !props?.longitude
  )
    return null;

  return (
    <FTModal
      visible={
        modalsState.showSendLocation || !props?.latitude || !props?.longitude
      }
      heading={t("heading")}
      onHide={hideModal}
      containerStyle="max-w-[calc(100vw-4rem)] w-[560px] !top-20 md:top-10"
    >
      <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
        <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
          {t("sub_heading")}
        </p>

        <AdminFormFieldInput
          label={t("phone_number")}
          type="text"
          id="phone"
          name="phone"
          placeholder={t("place_holder")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.phone}
          touched={touched.phone}
          error={errors.phone}
        />
        {MemoizedFormActionButtons}
        {/* <AdminFormFieldSubmit
              label={t('send')}
              variant="primary"
              disabled={!isFormValid || isSubmitting } // || isLoading
            />

            <AdminFormFieldSubmit
              label={tMain('cancel')}
              type="button"
              variant="danger-transparent"
              disabled={isSubmitting} // || isLoading
              onClick={hideModal}
            /> */}
      </form>
    </FTModal>
  );
};
export default ModalSendLocation;
